import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, AlertTriangle, ChefHat, Clock, Loader2, Plus, RefreshCw, Soup, Users,
} from 'lucide-react';
import apiClient from '../../services/api';
import { confirm, alertDialog } from '../../components/ui/confirm-dialog';

/* The counter during service: everything is derived, and the only thing staff create here is a refill request. */

const POLL_MS = 30000;

const fmtQty = (n, unit) => {
  const v = Number(n) || 0;
  // Pieces are counted, so a decimal point on them reads as a mistake.
  if (unit === 'pcs') return `${Math.round(v)} pcs`;
  return `${v % 1 === 0 ? v : v.toFixed(1)} kg`;
};

const t12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${ap}` : `${hr} ${ap}`;
};

const sinceLabel = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h} h ${mins % 60} min ago`;
};

/* A magnitude bar for how much of a dish has gone, amber past three quarters and red once nearly out. */
function ServedBar({ pct, low }) {
  const width = Math.max(0, Math.min(100, pct));
  const tone = low ? 'bg-red-500' : width >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-2 w-full rounded-full bg-slate-100" title={`${width}% served`}>
      <div className={`h-2 rounded-full ${tone}`} style={{ width: `${width}%` }} />
    </div>
  );
}

function Tile({ label, value, sub, tone = 'text-foreground' }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-xl font-bold tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function BuffetCounter({ restaurantId, buffet, date, onBack, embedded }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busyItem, setBusyItem] = useState(null);
  /* The batch quantity being typed, per dish. Held here rather than on the row
     because the row is replaced wholesale every poll — a number half-typed
     into the counter's own data would be wiped mid-keystroke. */
  const [batchQty, setBatchQty] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (quiet) => {
    if (!quiet) setRefreshing(true);
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/counter/`,
        { params: { date } },
      );
      if (!data?.success) throw new Error(data?.error || 'Failed to load the counter');
      setState(data.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load the counter');
    } finally {
      setRefreshing(false);
    }
  }, [restaurantId, buffet.id, date]);

  useEffect(() => { load(); }, [load]);
  /* The day is done — a past date, or a closed service. Everything below
     becomes the record of it: no batches, no asks, no stock flags. The server
     refuses those writes anyway; this stops the screen offering them. */
  const over = !!state?.over;
  // A counter goes stale fast, so someone else's reading should land here
  // without anyone pressing anything — but a finished day cannot change, so
  // it is not worth polling.
  useEffect(() => {
    if (over) return undefined;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [load, over]);

  /* Order another batch. ONE quantity drives the row: whatever is typed in the
     batch box is what the kitchen is asked for; left blank, it falls back to
     the pan size from the plan, and stays optional when none was planned. */
  const askQtyFor = (row) => {
    const typed = Number(batchQty[row.item]);
    return typed > 0 ? typed : (row.batch_qty > 0 ? row.batch_qty : 0);
  };
  const askForMore = async (row) => {
    const qty = askQtyFor(row);
    setBusyItem(row.item);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/refills/`,
        { item: row.item, unit: row.unit, date,
          ...(qty > 0 ? { qty } : {}) },
      );
      if (!data?.success) throw new Error(data?.error || 'Failed');
      if (data.counter) setState(data.counter);
      // The typed figure went to the kitchen — the box starts clean for the next one.
      setBatchQty((q) => ({ ...q, [row.item]: '' }));
    } catch (e) {
      await alertDialog({
        title: 'Could not send that to the kitchen',
        description: e?.response?.data?.error || e.message,
        tone: 'destructive',
      });
    } finally {
      setBusyItem(null);
    }
  };

  /* Log a batch that has ALREADY gone out — the counter's own entry, as
     opposed to asking the kitchen and waiting. Same ledger, written SENT on
     the spot, so the dish's total cooked moves the moment the pan does. The
     box is pre-filled with the pan size from the plan and typed over when the
     kitchen sent a different amount, which is most days. */
  const addBatch = async (row) => {
    const typed = Number(batchQty[row.item] ?? row.batch_qty ?? 0);
    if (!(typed > 0)) {
      await alertDialog({
        title: 'How much went out?',
        description: `Give ${row.item} a quantity — a batch with no amount tells the `
          + 'counter nothing about what is left.',
      });
      return;
    }
    setBusyItem(row.item);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/refills/`,
        { item: row.item, unit: row.unit, date, qty: typed, sent: true },
      );
      if (!data?.success) throw new Error(data?.error || 'Failed');
      if (data.counter) setState(data.counter);
      // Back to the pan size for the next one, rather than the number just used.
      setBatchQty((q) => ({ ...q, [row.item]: '' }));
    } catch (e) {
      await alertDialog({
        title: 'Could not add that batch',
        description: e?.response?.data?.error || e.message,
        tone: 'destructive',
      });
    } finally {
      setBusyItem(null);
    }
  };

  // Mark an open request as arrived - the moment the counter's arithmetic actually moves.
  const markSent = async (req) => {
    const ok = await confirm({
      title: 'Arrived at the counter?',
      description: `${req.item}${req.qty ? ` · ${fmtQty(req.qty, req.unit)}` : ''} — this adds it to the total cooked for the service.`,
      confirmText: 'Yes, it is out',
    });
    if (!ok) return;
    setBusyItem(req.item);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/refills/${req.id}/`,
        { action: 'sent' },
      );
      if (data?.counter) setState(data.counter);
    } catch (e) {
      await alertDialog({ title: 'Error', description: e?.response?.data?.error || e.message, tone: 'destructive' });
    } finally {
      setBusyItem(null);
    }
  };

  /* 86 a dish for this service, which also calls off any open refill request; undoable, because kitchens find another pan. */
  const setOutOfStock = async (row, out) => {
    if (out) {
      const ok = await confirm({
        title: `${row.item} — out of stock?`,
        description: 'It stays on the board marked OUT so the floor can answer guests, '
          + 'and any open kitchen request for it is called off. You can put it back '
          + 'if another pan turns up.',
        confirmText: 'Mark out of stock',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    setBusyItem(row.item);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/counter/stock/`,
        { item: row.item, unit: row.unit, out, date },
      );
      if (data?.counter) setState(data.counter);
    } catch (e) {
      await alertDialog({ title: 'Error', description: e?.response?.data?.error || e.message, tone: 'destructive' });
    } finally {
      setBusyItem(null);
    }
  };

  const cancelRequest = async (req) => {
    setBusyItem(req.item);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/refills/${req.id}/`,
        { action: 'cancelled' },
      );
      if (data?.counter) setState(data.counter);
    } catch {
      /* leaving it open is harmless — the next poll shows the truth */
    } finally {
      setBusyItem(null);
    }
  };

  const cap = state?.capacity || {};
  const items = state?.items || [];
  const lowCount = items.filter((i) => i.low).length;

  return (
    <div className={embedded ? '' : 'p-6 w-full'}>
      {/* The name, hours and way back are already above the tabs - repeating them would announce the page twice. */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        {!embedded && (
          <>
            <button onClick={onBack}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <ArrowLeft size={14} /> Buffets
            </button>
            <div>
              <h1 className="font-display text-2xl font-semibold leading-none tracking-tight">
                {buffet.name} — the counter
              </h1>
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          {cap.start_time || cap.end_time
            ? `${t12(cap.start_time)} – ${t12(cap.end_time)} · ` : ''}
          {state?.running ? 'being served now'
            : over ? 'service over — this is the record of the day'
              : 'service not running'}
        </p>
        <button onClick={() => load()}
          className="ml-auto inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {over && (
        <div className="mb-4 rounded-md border border-border bg-secondary/40 px-4 py-2.5 text-sm text-muted-foreground">
          This service day is over. What follows is the record of it — nothing can be
          added or changed here.
        </div>
      )}

      {!state ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Reading the counter…
        </p>
      ) : (
        <>
          {/* ── The room ─────────────────────────────────────────────── */}
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Users size={14} /> Capacity
          </h2>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile label="Covers sold" value={cap.covers_sold ?? 0}
              sub={`${cap.adults ?? 0} adults · ${cap.children ?? 0} kids`} />
            <Tile label="Planned for" value={cap.covers_planned || '—'} sub="expected covers" />
            <Tile label="Seats left" value={cap.seats_left ?? '—'}
              tone={cap.seats_left === 0 ? 'text-red-600' : 'text-foreground'}
              sub={cap.utilisation_pct != null ? `${cap.utilisation_pct}% of the room` : 'no plan to compare'} />
            <Tile label="Service elapsed" value={cap.elapsed_pct != null ? `${cap.elapsed_pct}%` : '—'}
              sub={cap.start_time ? `${t12(cap.start_time)} – ${t12(cap.end_time)}` : 'no window set'} />
            {/* The pair that matters: food gone against time gone. Ahead of the clock means the counter will not last. */}
            <Tile label="Food served" value={`${cap.food_served_pct ?? 0}%`}
              tone={(cap.elapsed_pct != null && cap.food_served_pct > cap.elapsed_pct + 15)
                ? 'text-amber-600' : 'text-foreground'}
              sub={cap.elapsed_pct != null
                ? (cap.food_served_pct > cap.elapsed_pct + 15
                  ? 'going faster than the clock'
                  : 'in step with the clock')
                : 'of what was cooked'} />
          </div>

          {(cap.elapsed_pct != null && cap.food_served_pct > cap.elapsed_pct + 15) && (
            <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{cap.food_served_pct}%</strong> of the food has gone with{' '}
                <strong>{100 - cap.elapsed_pct}%</strong> of the service still to run — at this
                rate the counter will not last. {lowCount ? `${lowCount} dish${lowCount === 1 ? '' : 'es'} already low.` : ''}
              </span>
            </div>
          )}

          {/* ── The dishes ───────────────────────────────────────────── */}
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Soup size={14} /> On the counter
            {lowCount > 0 && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                {lowCount} running low
              </span>
            )}
          </h2>

          {items.length === 0 ? (
            <div className="rounded-lg border border-border bg-white py-12 text-center text-sm text-muted-foreground">
              Nothing on the counter yet. Start the service from the buffet board — the
              quantities confirmed in its pre-flight are what appears here.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium min-w-[190px]">Dish</th>
                      <th className="px-4 py-2.5 text-left font-medium min-w-[150px]">
                        <span title="How far through the batch on the counter. Red once four fifths of it has gone — the point at which the kitchen still has time to answer.">Batch</span>
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium">
                        <span title="Still in the kitchen, waiting to be sent out as refills.">Left to refill</span>
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium">Served</th>
                      <th className="px-4 py-2.5 text-right font-medium">Total cooked</th>
                      <th className="px-4 py-2.5 text-right font-medium min-w-[200px]">Kitchen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      /* The whole row carries the warning, not just a number inside it - a counter is read at arm's length. */
                      <tr key={row.item}
                        className={`border-t border-border/60 align-top ${
                          row.out ? 'bg-red-50/40' : row.low ? 'bg-red-50/60' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-foreground">{row.item}</span>
                            {/* WHERE it stands — the floor plan's counter number(s), so
                                a batch is logged against "C3", not against a guess. */}
                            {(row.counters || []).map((n) => (
                              <span key={n}
                                title={`Standing at counter C${n} on this buffet's floor plan`}
                                className="rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-bold text-amber-800">
                                C{n}
                              </span>
                            ))}
                            {/* The dish's state rides with its name — its own column said little the Batch bar doesn't. */}
                            {row.out ? (
                              <span className="inline-block rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                                Out of stock
                              </span>
                            ) : row.low && (
                              <span className="text-[11px] font-semibold text-red-600">running low</span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {row.course || 'On the counter'}
                            {row.refilled > 0 && ` · ${fmtQty(row.refilled, row.unit)} refilled`}
                          </div>
                        </td>
                        {/* The pan, not the day: a batch four fifths gone is the thing to act on, and the only bar that turns red. */}
                        <td className="px-4 py-3">
                          {row.batch_qty > 0 ? (
                            <>
                              <ServedBar pct={row.batch_used_pct ?? 0} low={row.low} />
                              <div className={`mt-1 text-[11px] ${
                                row.low ? 'font-semibold text-red-600' : 'text-muted-foreground'}`}>
                                {row.batch_used_pct ?? 0}% of {fmtQty(row.batch_qty, row.unit)} gone
                              </div>
                            </>
                          ) : (
                            <>
                              <ServedBar pct={row.served_pct} low={row.low} />
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {row.served_pct}% gone · no batch size set
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {row.batch_qty > 0 ? fmtQty(row.in_kitchen, row.unit) : '—'}
                          {row.batch_qty > 0 && row.in_kitchen <= 0 && (
                            <div className="text-[11px] font-medium text-amber-700">
                              nothing held back
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {fmtQty(row.served, row.unit)}
                          {/* An unread dish is not an untouched one - say which, rather than letting 0 imply nobody ate it. */}
                          {row.never_read && (
                            <div className="text-[11px] text-amber-600">not read yet</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {fmtQty(row.cooked, row.unit)}
                          <div className="text-[11px] text-muted-foreground">
                            {row.refilled > 0
                              ? `${fmtQty(row.planned, row.unit)} planned · ${fmtQty(row.refilled, row.unit)} sent`
                              : `${fmtQty(row.left_total, row.unit)} left in all`}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {over ? (
                            /* Nothing to press: the day is over and its
                               figures are a record, not a work surface. */
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : row.out ? (
                            <button
                              type="button"
                              disabled={busyItem === row.item}
                              onClick={() => setOutOfStock(row, false)}
                              title="Another pan turned up — put it back on the board"
                              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              Back in stock
                            </button>
                          ) : row.open_request ? (
                            <div className="inline-flex flex-col items-end gap-1">
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                <Clock size={11} /> asked {sinceLabel(row.open_request.requested_at)}
                              </span>
                              <span className="flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={busyItem === row.item}
                                  onClick={() => markSent(row.open_request)}
                                  className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  It's out
                                </button>
                                <button
                                  type="button"
                                  disabled={busyItem === row.item}
                                  onClick={() => cancelRequest(row.open_request)}
                                  className="text-xs text-muted-foreground hover:underline"
                                >
                                  Cancel
                                </button>
                              </span>
                            </div>
                          ) : (
                            <div className="inline-flex flex-col items-end gap-1">
                              {/* The batch that has already gone out, typed and
                                  logged in one press. It sits ABOVE the ask,
                                  because during service the common event is a
                                  pan leaving the kitchen — not a plea for one. */}
                              {state.running && (
                                <span className="flex items-center gap-1">
                                  <input
                                    type="number" min="0" step={row.unit === 'pcs' ? '1' : '0.1'}
                                    value={batchQty[row.item] ?? ''}
                                    placeholder={row.batch_qty > 0 ? String(row.batch_qty) : '0'}
                                    onChange={(e) => setBatchQty((q) => ({ ...q, [row.item]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') addBatch(row); }}
                                    aria-label={`Batch quantity for ${row.item} in ${row.unit} — used by both Add batch and Ask the kitchen`}
                                    className="w-16 rounded-md border border-border px-2 py-1 text-right text-xs tabular-nums"
                                  />
                                  <span className="text-[11px] text-muted-foreground">{row.unit}</span>
                                  <button
                                    type="button"
                                    disabled={busyItem === row.item}
                                    onClick={() => addBatch(row)}
                                    title={`Log a batch of ${row.item} that has gone out${
                                      (row.counters || []).length
                                        ? ` to ${row.counters.map((n) => `C${n}`).join(' / ')}`
                                        : ' to the counter'}`}
                                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                  >
                                    <Plus size={12} /> Add batch
                                  </button>
                                </span>
                              )}
                              <button
                                type="button"
                                disabled={busyItem === row.item || !state.running}
                                onClick={() => askForMore(row)}
                                title={state.running
                                  ? (askQtyFor(row) > 0
                                    ? `Order the kitchen another batch — ${fmtQty(askQtyFor(row), row.unit)}${
                                      Number(batchQty[row.item]) > 0
                                        ? ', the amount in the box above'
                                        : ', the pan size from the plan (type in the box above to ask for a different amount)'}`
                                    : 'Tell the kitchen this needs topping up')
                                  : 'The service is not running'}
                                /* A pan four fifths gone is not a suggestion: the button becomes the thing on the row to press. */
                                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-50 ${
                                  row.low
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : 'border border-border text-slate-700 hover:bg-slate-50'}`}
                              >
                                {/* Named for who acts next, and priced by the SAME box as Add batch:
                                    one typed quantity, whether the pan is out already or still to cook. */}
                                <ChefHat size={13} /> Ask the kitchen{askQtyFor(row) > 0 ? ` · ${fmtQty(askQtyFor(row), row.unit)}` : ''}
                              </button>
                              <button
                                type="button"
                                disabled={busyItem === row.item}
                                onClick={() => setOutOfStock(row, true)}
                                className="text-[11px] font-medium text-red-600 hover:underline disabled:opacity-50"
                              >
                                Out of stock
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── The refill log ───────────────────────────────────────── */}
          {(state.requests || []).length > 0 && (
            <>
              <h2 className="mb-2 mt-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
                <ChefHat size={14} /> {over ? 'Refills asked that day' : 'Refills asked for today'}
              </h2>
              <div className="overflow-hidden rounded-lg border border-border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium">Dish</th>
                      <th className="px-4 py-2.5 text-left font-medium">Asked</th>
                      <th className="px-4 py-2.5 text-left font-medium">By</th>
                      <th className="px-4 py-2.5 text-left font-medium">Answered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.requests.map((r) => (
                      <tr key={r.id} className="border-t border-border/60">
                        <td className="px-4 py-2.5 font-medium">
                          {r.item}
                          {r.qty ? <span className="ml-1 text-muted-foreground">· {fmtQty(r.qty, r.unit)}</span> : null}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                          {new Date(r.requested_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{r.requested_by_name || '—'}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {r.status === 'SENT' ? (
                            <span className="text-emerald-700">
                              sent {new Date(r.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {r.waited_minutes != null && (
                                <span className="text-muted-foreground"> · waited {r.waited_minutes} min</span>
                              )}
                            </span>
                          ) : r.status === 'CANCELLED' ? (
                            <span className="text-muted-foreground">called off</span>
                          ) : (
                            <span className="text-amber-700">waiting on the kitchen</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            Figures are as fresh as the last consumption reading.
          </p>
        </>
      )}
    </div>
  );
}
